import { useEffect, useRef, useState } from "react";
import type { LucideIcon } from "lucide-react";

/* شريط أدوات جانبي بشرائح (Slides): كل شريحة 5 أدوات، اسحب أفقياً للتنقل،
   ونقاط تحت الشريط تبيّن الشريحة الحالية. المؤشر ينزلق للأداة النشطة. */

export interface DockTool {
  key: string;
  label: string;
  Icon: LucideIcon;
  badge?: boolean | number;
  /** إذا موجودة: زر إجراء (مثل فتح أداة الرسم) وما يصير "نشط" */
  onClick?: () => void;
}

const ITEM_H = 50;
const GAP = 3;
const PAD = 5;
const PER_PAGE = 5;
const PAGE_H = PER_PAGE * ITEM_H + (PER_PAGE - 1) * GAP;

export function ToolDock({
  pages,
  activeKey,
  onSelect,
}: {
  pages: DockTool[][];
  activeKey: string | null;
  onSelect: (key: string) => void;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  const multi = pages.length > 1;

  const sign = () => (scrollerRef.current && getComputedStyle(scrollerRef.current).direction === "rtl" ? -1 : 1);
  const goTo = (i: number, smooth = true) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: sign() * i * el.clientWidth, behavior: smooth ? "smooth" : "auto" });
  };

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el || !el.clientWidth) return;
    setPage(Math.max(0, Math.min(pages.length - 1, Math.round(Math.abs(el.scrollLeft) / el.clientWidth))));
  };

  // لما تتحدد أداة من شريحة ثانية (برمجياً) نروح لشريحتها
  const activePage = pages.findIndex((p) => p.some((t) => t.key === activeKey));
  useEffect(() => {
    if (activePage >= 0 && activePage !== page) goTo(activePage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  // تلميح مرة وحدة: نحرّك الشريط شوية حتى ينتبه المستخدم إن في شريحة ثانية
  useEffect(() => {
    if (!multi) return;
    let seen = false;
    try {
      seen = localStorage.getItem("sh_dock_hint") === "1";
      localStorage.setItem("sh_dock_hint", "1");
    } catch {
      /* ignore */
    }
    if (seen) return;
    const t1 = window.setTimeout(() => scrollerRef.current?.scrollTo({ left: sign() * 22, behavior: "smooth" }), 900);
    const t2 = window.setTimeout(() => goTo(0), 1500);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multi]);

  return (
    <nav
      aria-label="الأدوات"
      className="sticky shrink-0 self-start rounded-[20px]"
      style={{
        top: 64,
        width: 52,
        padding: PAD,
        background: "linear-gradient(180deg, rgba(255,255,255,.11), rgba(255,255,255,.04))",
        border: "1px solid rgba(255,255,255,.13)",
        boxShadow: "0 14px 34px -12px rgba(0,0,0,.7), inset 0 1px 0 rgba(255,255,255,.14)",
      }}
    >
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="flex overflow-x-auto no-scrollbar"
        style={{ scrollSnapType: "x mandatory", scrollbarWidth: "none", height: multi ? PAGE_H : undefined, touchAction: "pan-x" }}
      >
        {pages.map((tools, pi) => {
          const idx = tools.findIndex((t) => !t.onClick && t.key === activeKey);
          return (
            <div key={pi} className="shrink-0 w-full relative" style={{ scrollSnapAlign: "start", minHeight: PAGE_H }}>
              {idx >= 0 && (
                <div
                  className="absolute inset-x-0 rounded-2xl pointer-events-none"
                  style={{
                    height: ITEM_H,
                    top: idx * (ITEM_H + GAP),
                    background: "linear-gradient(145deg, #4C8DFF, #2F5BEA)",
                    boxShadow: "0 8px 20px -6px rgba(59,130,246,.8), inset 0 1px 0 rgba(255,255,255,.35)",
                    transition: "top .28s cubic-bezier(.3,1.3,.5,1)",
                  }}
                />
              )}
              <div className="relative flex flex-col" style={{ gap: GAP }}>
                {tools.map((t) => {
                  const active = !t.onClick && t.key === activeKey;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => (t.onClick ? t.onClick() : onSelect(t.key))}
                      aria-label={t.label}
                      aria-pressed={active}
                      className="relative flex flex-col items-center justify-center gap-1 rounded-2xl active:scale-95 transition-transform"
                      style={{ height: ITEM_H, color: active ? "#fff" : "rgba(255,255,255,.62)" }}
                    >
                      <t.Icon size={19} strokeWidth={active ? 2.4 : 1.9} />
                      <span className="text-[9px] leading-none font-medium">{t.label}</span>
                      {t.badge ? (
                        typeof t.badge === "number" ? (
                          <span
                            className="absolute top-0.5 end-1 min-w-[14px] h-[14px] px-1 rounded-full text-[9px] font-bold flex items-center justify-center"
                            style={{ background: active ? "#fff" : "#3B82F6", color: active ? "#2F5BEA" : "#fff" }}
                          >
                            {t.badge}
                          </span>
                        ) : (
                          <span
                            className="absolute top-1.5 end-2 w-1.5 h-1.5 rounded-full"
                            style={{ background: active ? "#fff" : "#2FE6B8", boxShadow: "0 0 0 2px rgba(0,0,0,.25)" }}
                          />
                        )
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {multi && (
        <div className="flex items-center justify-center gap-1.5 pt-2.5 pb-0.5">
          {pages.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`الشريحة ${i + 1}`}
              onClick={() => goTo(i)}
              className="rounded-full transition-all duration-200"
              style={{
                height: 5,
                width: i === page ? 16 : 5,
                background: i === page ? "var(--tg-theme-button-color, #3B82F6)" : "rgba(255,255,255,.3)",
              }}
            />
          ))}
        </div>
      )}
    </nav>
  );
}
